import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import type { AxiosResponse } from 'axios';
import { LeenOnRamp } from '@leendev/onramp';
import { Drawer, LoadingOverlay, Stack, TextInput, Text } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';

import { StudioSkeleton } from '../../pages/moo/studio-components';
import ConnectedTile from './ConnectedTile';
import ConnectorQuickViewContainer from './ConnectorQuickViewContainer';
import AvailableTile from './AvailableTile';
import LeenConnectorTile from './connectorsV2/LeenCoonnectorTile';
import ConnectorSection from './ConnectorSection';
import {
  availableConnectors,
  categoryOrder,
  categoryTag,
  emptyCopy,
  enrichConnections,
  groupConnectionsByType,
  sortByCategory,
  visibleAvailable,
  visibleConnected,
  type Connection,
  type Connector,
} from './connectors.view';
import styles from './Connectors.module.css';

// service
import {
  getLegacyConnectors,
  leenOrgCreate,
  leenTokenCreate,
  storeLeenConnectionResponse,
  getConnections,
} from './ConnectorService';
import { fetchAccountDetailsById } from '../../accounts/AccountService';
import { PubSub } from '../../utils/eventUtils';

// hooks
import { useActor } from '../../components/hooks/useActor';
import useNotifications from '../../_lib/notifications/useNotifications';

/** Connectors whose tile opens the quick-view panel. */
const QUICK_VIEW_TYPES = ['MICROSOFT'];

const ConnectorsPage = () => {
  const notifications = useNotifications();
  const history = useHistory();
  const [searchText, setSearchText] = useState('');
  const [showFailedSnackbar, setShowFailedSnackbar] = useState<boolean>(false);
  const [cbid, setCbid] = useState<string | null>(null);
  // Organization ID in Leen
  const [, setOrgId] = useState(null);
  const [connectionsData, setConnectionsData] =
    useState<ConnectionsData | null>(null);
  // control opening and closing of Leen modal
  const [isConnectorModalOpen, setIsConnectorModalOpen] = useState(false);
  // active connector
  const [activeConnector, setActiveConnector] = useState<string | undefined>(
    undefined
  );
  // the connection whose quick-view panel is open
  const [quickView, setQuickView] = useState<Connection[] | null>(null);
  // Refresh state
  const [refresh, setRefresh] = useState(0);
  const [isConnectorsAPILoading, setIsConnectorsAPILoading] = useState(false);
  const [isTokenAPILoading, setIsTokenAPILoading] = useState(false);
  // Token for Leen onRamp
  const [leenToken, setLeenToken] = useState(null);
  const [onRampResponse, setOnRampResponse] = useState<any>(undefined); // TODO: define type for onRamp when confirmed

  const actor = useActor();
  const accountId = actor.account.id;

  // Subscribe to refresh event
  useEffect(() => {
    const refetchSub = PubSub.subscribe('connector-page-refetch', () => {
      setRefresh((prev) => prev + 1);
    });
    return () => {
      refetchSub.remove();
    };
  }, []);

  // fetch Connections
  useEffect(() => {
    if (showFailedSnackbar) {
      notifications.error('Failed to connect to this connector');
      setShowFailedSnackbar(false);
    }

    if (accountId) {
      setIsConnectorsAPILoading(true);
      Promise.all([
        getConnections(),
        getLegacyConnectors(),
        fetchAccountDetailsById(accountId),
      ])
        .then((responses) => {
          const connections: Connection[] = responses[0].data.content;
          const connectors: Connector[] = responses[1].data;

          setConnectionsData({
            connected: groupConnectionsByType(
              enrichConnections(connections, connectors)
            ),
            available: availableConnectors(connections, connectors),
          });
          setCbid(responses[2].data.cbid);
        })
        .catch(console.error.bind(console))
        .finally(() => {
          setIsConnectorsAPILoading(false);
        });
    }
  }, [accountId, notifications, showFailedSnackbar, refresh]);

  const handleLeenConnectClick = useCallback((value: string) => {
    // drop the previous vendor's token first: LeenOnRamp reads `token` only on
    // mount, so letting a stale one survive here renders an empty widget
    setLeenToken(null);
    setActiveConnector(value);
    setIsConnectorModalOpen(true);
  }, []);

  const connected = useMemo(
    () => visibleConnected(connectionsData?.connected ?? [], searchText),
    [connectionsData, searchText]
  );
  const available = useMemo(
    () => visibleAvailable(connectionsData?.available ?? [], searchText),
    [connectionsData, searchText]
  );
  // Ranked over everything on the page before search narrows it.
  const order = useMemo(
    () =>
      categoryOrder([
        ...(connectionsData?.connected ?? []).map(([first]) => first.category),
        ...(connectionsData?.available ?? []).map((item) => item.category),
      ]),
    [connectionsData]
  );

  // #region Leen Connectors

  // Create organization in Leen and get the organization ID
  useEffect(() => {
    // call leenOrgCreate to create a new organization  in Leen if cbid exists
    if (cbid) {
      leenOrgCreate({}, null)
        .then((response: AxiosResponse) => {
          if (response.data?.leenOrgId) {
            setOrgId(response.data.leenOrgId);
          }
        })
        .catch((error: Error) => {
          notifications.error(
            `Error creating / fetching organization for connector: ${error}`
          );
        });
    }
  }, [cbid, notifications]);

  // Create token in Leen once user clicks on the connector tile and accepts the terms
  useEffect(() => {
    if (isConnectorModalOpen && activeConnector) {
      setIsTokenAPILoading(true);
      leenTokenCreate({}, { vendor: activeConnector })
        .then((response: AxiosResponse) => {
          if (response.data?.token) {
            setLeenToken(response.data.token);
          }
          setIsTokenAPILoading(false);
        })
        .catch((error: Error) => {
          notifications.error(`Error creating token: ${error}`);
          setIsTokenAPILoading(false);
        });
    }
  }, [cbid, isConnectorModalOpen, notifications, activeConnector]);

  // Store the connection response returned by Leen OnRamp to Cowbell Systems
  useEffect(() => {
    if (onRampResponse) {
      onRampResponse.data.identifier = onRampResponse.data.identifier || '';
      onRampResponse.data.oauth2_authorize_url =
        onRampResponse.data.oauth2_authorize_url || '';
      const payload = {
        connector: activeConnector,
        provider: 'LEEN',
        credentials: null,
        cbid,
        metadata: {
          ...onRampResponse.data,
        },
      };
      storeLeenConnectionResponse({}, payload)
        .then(() => {
          setShowFailedSnackbar(false);
          notifications.success('Successfully connected');
          setOnRampResponse(undefined);
          setActiveConnector(undefined);
          setIsConnectorModalOpen(false);
          setRefresh((prev) => prev + 1);
        })
        .catch((error: Error) => {
          notifications.error(`Failed to connect to this connector: ${error}`);
          setOnRampResponse(undefined);
        });
    }
  }, [onRampResponse, notifications, cbid, activeConnector]);

  // #endregion

  const renderConnected = (group: Connection[]) => {
    const [first] = group;
    if (first.provider === 'LEEN') {
      return (
        <LeenConnectorTile
          key={first.connectorType}
          data={{ ...first, status: first.connectorListStatus }}
          handleClick={handleLeenConnectClick}
          tag={categoryTag(first.category, order)}
        />
      );
    }
    return (
      <ConnectedTile
        key={first.connectorType}
        dataArr={group}
        cbid={cbid}
        tag={categoryTag(first.category, order)}
        onViewDetails={
          QUICK_VIEW_TYPES.includes(first.connectorType)
            ? setQuickView
            : undefined
        }
      />
    );
  };

  const renderAvailable = (connector: Connector) =>
    connector.provider === 'LEEN' ? (
      <LeenConnectorTile
        key={connector.name.toLowerCase()}
        data={connector}
        handleClick={handleLeenConnectClick}
        tag={categoryTag(connector.category, order)}
      />
    ) : (
      <AvailableTile
        key={connector.name.toLowerCase()}
        data={connector}
        cbid={cbid}
        tag={categoryTag(connector.category, order)}
      />
    );

  return (
    <Stack gap="xl" className={styles.page}>
      <header className={styles.header}>
        <div>
          {/* The shared page heading, as on My Agent, Quotes and Claims. */}
          <h1 className={`table-heading ${styles.pageTitle}`}>Connectors</h1>
          <Text c="dimmed" size="sm">
            Connect the security tools you already use so Cowbell can see your
            security posture.
          </Text>
        </div>
        <TextInput
          className={styles.search}
          placeholder="Search connectors"
          aria-label="Search connectors"
          leftSection={<FontAwesomeIcon icon={faMagnifyingGlass} />}
          value={searchText}
          onChange={(event) => setSearchText(event.currentTarget.value)}
        />
      </header>

      {isConnectorsAPILoading && !connectionsData && (
        <StudioSkeleton variant="cards" count={6} />
      )}

      {connectionsData && (
        <>
          <ConnectorSection
            title="Connected"
            count={connected.length}
            empty={emptyCopy('connected', searchText)}
          >
            {connected.length > 0 && (
              <div className={styles.grid}>
                {sortByCategory(
                  connected,
                  ([first]) => first.category,
                  order
                ).map(renderConnected)}
              </div>
            )}
          </ConnectorSection>

          <ConnectorSection
            title="Available"
            count={available.length}
            empty={emptyCopy('available', searchText)}
          >
            {available.length > 0 && (
              <div className={styles.grid}>
                {sortByCategory(available, (item) => item.category, order).map(
                  renderAvailable
                )}
              </div>
            )}
          </ConnectorSection>
        </>
      )}

      <Drawer
        opened={!!quickView}
        onClose={() => setQuickView(null)}
        position="right"
        size="auto"
        title={quickView?.[0].name}
      >
        {quickView && (
          <ConnectorQuickViewContainer
            open
            dataArr={quickView}
            history={history}
          />
        )}
      </Drawer>

      {isConnectorModalOpen && leenToken && (
        <LeenOnRamp
          key={leenToken}
          token={leenToken}
          setLeenOnRampResponse={setOnRampResponse}
          setShowLeenOnRamp={setIsConnectorModalOpen}
        />
      )}
      <LoadingOverlay
        visible={
          isTokenAPILoading || (isConnectorsAPILoading && !!connectionsData)
        }
      />
    </Stack>
  );
};

interface ConnectionsData {
  connected: Connection[][];
  available: Connector[];
}

export default ConnectorsPage;
